/* Slider boject proxy.
10/6/2020 Created RLB

Generally matches the AS3 slider component behavior
	enabled : Boolean
	liveDragging : Boolean. Whether the SliderEvent.CHANGE event is dispatched continuously as the user moves the slider thumb.
	maximum : Number  The maximum allowed value on the Slider component instance.
	minimum : Number  The minimum value allowed on the Slider component instance.
	snapInterval : Number Increment by which the value is increased or decreased as the user moves the slider thumb.
	tickInterval : Number  The spacing of the tick marks relative to the maximum value of the component.
	value : Number  The current value of the Slider component.
	enableWheel: boolean

	Events thrown:
	SLIDERVALUECHANGE
	SLIDERMOUSEDOWN
	SLIDERMOUSEUP
*/
import AnimateProxyBase from "SATPROF_LIBRARY/DropinSupervisor/animate-proxys/animate-proxy-base";
import AnimateButtonProxy from "SATPROF_LIBRARY/DropinSupervisor/animate-proxys/animate-button-proxy";
import TimingInfo from "SATPROF_LIBRARY/DropinSupervisor/timing-info";
import AnimateRenderSpace from "SATPROF_LIBRARY/DropinSupervisor/render-spaces/animate-render-space";
import UtilSP from "SATPROF_UTILS/UtilSP"; 
import { StringKeyframeTrack, AudioAnalyser } from "three";

export default class Slider extends AnimateProxyBase{
 
	private mouseAtStartDrag:createjs.Point; // In parent's space
	private handleAtStartDrag:createjs.Point; // In parent's space
	private mouseNow:createjs.Point;
	private ready:boolean = false;

	// Settable properties:
	private _minimum:number = 0; // Min allowable value; (Settable)
	private _maximum:number = 10; // Max allowable value returned; (Settable)
	private _enabled:boolean = true; // Prevents user from dragging it
	private _snap:boolean = false; // 
	private _snapInterval:number = .1; //
	private _tickInterval:number = 0; //
	private _value:number = 5;
	private _dragging:boolean = false;
	private _trackLength:number = 100;
	private _scale:createjs.Shape;
	public name:string = "";
	private _lastValue:number = -99e99
	private _enableWheel:boolean = true;
	private _wheelSensitivity:number = 1;
    protected _onClickCallback : (e: MouseEvent) =>void = null;

    onFrameZero(){
        //console.log("In Slider.onFrameZero()");
        super.onFrameZero();
        const animate_rs:AnimateRenderSpace = this.extraData.animateSpace;
        //this.stageSymbol.outline_mc.rotation = 20;
        this.initialize();

    }
	public initialize(){

		//console.log("FYI, in Slider_p.ts, finished initializing.");
       	this.ready = true;
		this.stageSymbol.handle.cursor = "pointer";
		this.stageSymbol.handle.addEventListener("click", this.clickHandler.bind(this));
		this.stageSymbol.handle.addEventListener("mousedown", this.startDrag.bind(this));
		this.stageSymbol.handle.addEventListener("pressmove", this.dragHandler.bind(this));
		this.stageSymbol.handle.addEventListener("pressup", this.stopDrag.bind(this));
		this.stageSymbol.placeholder.visible = false;
		//window.addEventListener("wheel", console.log);
		window.addEventListener("wheel", this.mouseWheelHandler.bind(this));

		this._scale = new createjs.Shape();
		this.stageSymbol.addChild(this._scale);
		this._scale.visible = true;
		this.redrawScale();
    }  
    onRequestAnimFrame(timingInfo : TimingInfo) {
        // Do every frame
	} 
    public onButtonClick(eventCallback : (e:MouseEvent)=>void ){
        // Allows external function to store a callback function for later use.
        // Define _onPressCallback to be whatever the external function wants saved.
        this._onClickCallback = eventCallback;
    }	
	public clickHandler(e:MouseEvent){
		if(this._onClickCallback){
            // A callback function exists; it must have been stored by an external function 
            // Run that callback function:
            this._onClickCallback(e);		
		}
	}	 
	startDrag(evt:any){
		if (!this._enabled){
			return;
		}
		//console.log("In startDrag");
		this._dragging = true;
		this.mouseAtStartDrag = this.stageSymbol.handle.globalToLocal(evt.stageX, evt.stageY);
		this.handleAtStartDrag = new createjs.Point(this.stageSymbol.handle.x,this.stageSymbol.handle.y); 
		const event = new CustomEvent('SLIDERMOUSEDOWN', { detail:{name:this.name} });
		window.dispatchEvent(event);
	}
	dragHandler(evt:any){
		if (!this._enabled){
			return;
		}		//console.log("In dragHandler");
		this.mouseNow = this.stageSymbol.globalToLocal(evt.stageX, evt.stageY);
		let xNew:number = this.mouseNow.x;
		let vNewRequested:number = this.x2val(xNew);
		this.update(vNewRequested);

	}
	stopDrag(evt:any){
		////dispatchEvent(new CustomEvent("DIAL_RELEASED",false,false,arg1text,arg2));
		//console.log("In stopDrag")
		const event = new CustomEvent('SLIDERMOUSEUP', { detail:{name:this.name} });
		window.dispatchEvent(event);
		this._dragging = false;
	}

	public mouseWheelHandler(event:WheelEvent) {
		if (!this._enableWheel){
			return;
		}
		let del:number;
		let vNewRequested:number;
		if (this._snapInterval > 0){
			if (event.deltaY > 0){
				vNewRequested = this.value - this._wheelSensitivity*this._snapInterval;
			} else {
				vNewRequested = this.value + this._wheelSensitivity*this._snapInterval;
			}
			//console.log("In mouseWheelHandler, _snapInterval = "+this._snapInterval+", vNewRequested = "+vNewRequested);
		} else {
			del = this._wheelSensitivity * 0.01 * (event.deltaY/100) * (this._maximum - this._minimum);
			vNewRequested = this.value + del;
			//console.log("In mouseWheelHandler, del = "+del+", vNewRequested = "+vNewRequested);
		}
		this.update(vNewRequested);
	}
	
	private update(vNewRequested:number):void{
		this.value = vNewRequested; // Use setter
		//console.log("In slider update  this.value = "+this.value);
		if (this.value != this._lastValue){
			const event = new CustomEvent('SLIDERVALUECHANGE', { detail:{name:this.name} });
			window.dispatchEvent(event);
		}
		this._lastValue = this.value;
	}
	public set value(v:number){
		if (this._snapInterval > 0){
			this._value = this._snapInterval * Math.round(v / this._snapInterval);
		} else {
			this._value = v;
		}
		this._value = UtilSP.minmax(this._value, this._minimum, this._maximum);
		let xPos:number = this.val2x(this._value);
		this.stageSymbol.handle.x = xPos;
	}
	public get value():number{
		return this._value;
	}
	private val2x(v:number):number{
		let x:number = (this._trackLength/(this._maximum - this._minimum))*(v - this._minimum);
		return x;
	}
	private x2val(x:number):number{
		let val:number = this._minimum + x * (this._maximum - this._minimum)/this._trackLength;
		return val;
	}	
	private redrawScale():void{
		this._scale.graphics.clear();
		this._scale.graphics.setStrokeStyle(2); // Width
		this._scale.graphics.beginStroke("dimgray" ); // Color
	   	this._scale.graphics.moveTo(0,0);
		this._scale.graphics.lineTo(this._trackLength,0);
		this._scale.graphics.beginStroke("lightgrey" ); // Color
	   	this._scale.graphics.moveTo(0,3);
		this._scale.graphics.lineTo(this._trackLength,3);
		
		if (this._tickInterval > 0){
			let nTicks:number = (this._maximum - this._minimum)/this._tickInterval;
			let xSpacing:number = this._tickInterval * this._trackLength/(this._maximum - this._minimum);
			let val0:number = this._tickInterval*Math.round(this._minimum/this._tickInterval);
			let x0:number = this.val2x(val0);
			let x:number;
			this._scale.graphics.beginStroke("dimgray" ); // Color
			//console.log("nTicks = "+nTicks+", val0 = "+val0+", min = "+this._minimum+", max = "+this._maximum+", x0 = "+x0+", xSpacing = "+xSpacing);
			for (let i=0; i<=nTicks; i++){
				x = x0 + i*xSpacing;
				this._scale.graphics.moveTo(x,-3);
				this._scale.graphics.lineTo(x,-5);
				//console.log(i+"  "+x);
			}
		}

	}
	//-----------------------------------------------------------------------------
	
	// Setters and getters
	public set trackLength(val:number){
		this._trackLength = val;
		this.redrawScale();
	}
	public get trackLength():number{
		return this._trackLength;
	}
	public set tickInterval(val:number){
		this._tickInterval = val;
		this.redrawScale();
	}
	public get tickInterval():number{
		return this._tickInterval;
	}	
	public set minimum(val:number){
		this._minimum = val;
		this.redrawScale();
		this.value = this._value; // Update handle position
	}
	public get minimum():number{
		return this._minimum;
	}	
	public set maximum(val:number){
		this._maximum = val;
		this.redrawScale();
		this.value = this._value; // Update handle position
	}
	public get maximum():number{
		return this._maximum;
	}
	public set snapInterval(val:number){
		this._snapInterval = val;
		this.value = this._value; // Update handle position
	}
	public get snapInterval():number{
		return this._snapInterval;
	}
	public set enabled(val:boolean){
		this._enabled = val;
		if (!this._enabled){
			this._dragging = false;
		}
	}
	public get enabled():boolean{
		return this._enabled;
	}	
	public set showScale(val:boolean){
		this._scale.visible = val;
	}
	public get showScale():boolean{
		return this._scale.visible;
	}
	public set enableWheel(val:boolean){
		this._enableWheel = val;
	}
	public get enableWheel():boolean{
		return this._enableWheel;
	}
	public set wheelSensitivity(val:number){
		this._wheelSensitivity = val;
	}
	public get wheelSensitivity():number{
		return this._wheelSensitivity;
	}
	public set visible(val:boolean){
		this.stageSymbol.visible = val;
	}				
	public get visible(){
		return this.stageSymbol.visible;
	}
	

}

